import React, { useState } from 'react';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Delete, Edit, Visibility } from '@mui/icons-material';

import { useMember } from '../../context/MemberContext';
import { useTeam } from '../../context/TeamContext';
import ConfirmDeleteDialog from '../../components/ConfirmDialog';
import AddMemberButton from './AddMemberDialog';
import UserForm from './UserForm';
import { handleFullName } from './helper';

const Members = () => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmDeleteMember, setConfirmDeleteMember] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const { members, ...memberActions } = useMember();
  const { teams } = useTeam();

  const handleView = (member) => {
    setSelectedMember(member);
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleEdit = (member) => {
    setSelectedMember(member);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleSave = (payload) => {
    memberActions.updateMember({
      ...payload,
      id: selectedMember.id,
      full_name: handleFullName(payload),
    });
    setOpenDialog(false);
  };

  const handleCloseModal = () => {
    setSelectedMember(null);
    setOpenDialog(false);
  };

  const handleAddMember = (payload) => {
    memberActions.addMember({
      ...payload,
      full_name: handleFullName(payload),
    });
  };

  const handleConfirmDeleteMember = () => {
    if (confirmDeleteMember) {
      memberActions.removeMember(confirmDeleteMember.id);
      setConfirmDeleteMember(null);
    }
  };

  const columns = [
    { field: 'full_name', headerName: 'Name', flex: 1, minWidth: 120 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 120 },
    { field: 'phone', headerName: 'Phone', width: 120 },
    {
      field: 'dob',
      headerName: 'Date of birth',
      width: 120,
      valueFormatter: (params) => {
        const valueFormatted = new Date(params.value).toLocaleDateString();
        return valueFormatted;
      },
    },
    {
      field: 'start_date',
      headerName: 'Joined date',
      width: 120,
      valueFormatter: (params) => {
        const valueFormatted = new Date(params.value).toLocaleDateString();
        return valueFormatted;
      },
    },
    {
      field: 'team',
      headerName: 'Team',
      width: 120,
      valueFormatter: (params) => {
        const team = teams.find((item) => item.id === params.value);
        return team?.name || '-';
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ marginLeft: '-8px' }}>
          <IconButton color="primary" onClick={() => handleView(params.row)}>
            <Visibility />
          </IconButton>
          <IconButton color="secondary" onClick={() => handleEdit(params.row)}>
            <Edit />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => setConfirmDeleteMember(params.row)}>
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ padding: '20px' }}>
      <Box>
        <AddMemberButton addMemberToTeam={handleAddMember} />
      </Box>
      <Box sx={{ minHeight: 600, width: '100%' }}>
        <DataGrid
          rows={members}
          columns={columns}
          pageSize={pageSize}
          s
          rowsPerPageOptions={[10, 20, 50]}
          onPageSizeChange={setPageSize}
          disableSelectionOnClick
          autoHeight
        />
      </Box>

      <ConfirmDeleteDialog
        isOpen={!!confirmDeleteMember}
        title="Delete Member"
        confirmText={`Are you sure want to remove member "${confirmDeleteMember?.full_name}"?`}
        onClose={() => setConfirmDeleteMember(null)}
        onConfirm={handleConfirmDeleteMember}
      />

      {/* Dialog for View/Edit */}
      <Dialog open={openDialog} onClose={handleCloseModal}>
        <DialogTitle>
          {editMode ? 'Edit Member' : 'View Member Details'}
        </DialogTitle>
        <DialogContent>
          <UserForm
            onCancel={handleCloseModal}
            onSubmit={handleSave}
            user={selectedMember}
            isViewOnly={!editMode}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Members;
